import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';

interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectProps
  extends Omit<
    React.SelectHTMLAttributes<HTMLSelectElement>,
    'children' | 'onChange' | 'value' | 'defaultValue'
  > {
  label?: string;
  options: SelectOption[];
  placeholder?: string;
  error?: string;
  helperText?: string;
  value?: string;
  defaultValue?: string;
  // Loose event shape so existing call-sites passing a real ChangeEvent handler still type-check.
  // We synthesise an object with a matching `target.value` so callers using `e.target.value` keep working.
  onChange?: (event: any) => void;
}

export function Select({
  label,
  options,
  placeholder,
  error,
  helperText,
  className,
  id,
  disabled,
  value,
  defaultValue,
  onChange,
  name,
  required,
}: SelectProps) {
  const [internalValue, setInternalValue] = useState<string>(
    value ?? defaultValue ?? '',
  );
  const [isOpen, setIsOpen] = useState(false);
  // Option the keyboard is on while the list is open. Focus never leaves the
  // trigger; `aria-activedescendant` tells assistive tech which option that is
  // (the WAI-ARIA "select-only combobox" pattern).
  const [activeIndex, setActiveIndex] = useState(-1);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentValue = value !== undefined ? value : internalValue;
  const selectedOption = options.find((o) => o.value === currentValue);
  const generatedId = React.useId();
  const selectId = id || label?.toLowerCase().replace(/\s+/g, '-') || generatedId;
  const labelId = `${selectId}-label`;
  const listboxId = `${selectId}-listbox`;
  const messageId = `${selectId}-message`;
  const optionId = (index: number) => `${selectId}-option-${index}`;

  const hasOptions = options.length > 0;
  const effectiveDisabled = disabled || !hasOptions;
  const effectivePlaceholder = !hasOptions
    ? 'No options available'
    : placeholder || 'Select...';

  // Position the dropdown relative to the trigger and keep it in view
  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) return;

    const updatePosition = () => {
      const rect = triggerRef.current!.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropdownMaxHeight = 240;
      const openUpward = spaceBelow < dropdownMaxHeight + 8 && rect.top > dropdownMaxHeight;

      setDropdownStyle({
        position: 'fixed',
        left: rect.left,
        width: rect.width,
        top: openUpward ? undefined : rect.bottom + 4,
        bottom: openUpward ? window.innerHeight - rect.top + 4 : undefined,
        zIndex: 9999,
      });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      const insideTrigger = containerRef.current?.contains(target);
      const insideDropdown = dropdownRef.current?.contains(target);
      if (!insideTrigger && !insideDropdown) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option: SelectOption) => {
    if (option.disabled) return;
    if (value === undefined) {
      setInternalValue(option.value);
    }
    onChange?.({ target: { value: option.value } });
    setIsOpen(false);
  };

  // Next enabled option from `from` in direction `step`, or `from` if none.
  const nextEnabled = (from: number, step: 1 | -1): number => {
    for (let i = from + step; i >= 0 && i < options.length; i += step) {
      if (!options[i].disabled) return i;
    }
    return from;
  };
  const firstEnabled = () => nextEnabled(-1, 1);
  const lastEnabled = () => nextEnabled(options.length, -1);

  const open = () => {
    if (effectiveDisabled) return;
    const selectedIndex = options.findIndex((o) => o.value === currentValue);
    setActiveIndex(selectedIndex >= 0 && !options[selectedIndex].disabled ? selectedIndex : firstEnabled());
    setIsOpen(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (effectiveDisabled) return;
    if (!isOpen) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
        open();
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((i) => nextEnabled(i, 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((i) => nextEnabled(i, -1));
        break;
      case 'Home':
        e.preventDefault();
        setActiveIndex(firstEnabled());
        break;
      case 'End':
        e.preventDefault();
        setActiveIndex(lastEnabled());
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (activeIndex >= 0) handleSelect(options[activeIndex]);
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        break;
      case 'Tab':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  // Keep the keyboard's option in view in a long list.
  useEffect(() => {
    if (!isOpen || activeIndex < 0) return;
    document.getElementById(optionId(activeIndex))?.scrollIntoView?.({ block: 'nearest' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeIndex]);

  const hasMessage = !!error || !hasOptions || (!!helperText && hasOptions);

  return (
    <div className="w-full" ref={containerRef}>
      {label && (
        <label
          id={labelId}
          htmlFor={selectId}
          className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200"
        >
          {label}
        </label>
      )}

      {/* Hidden native input for form submission compatibility */}
      {name && <input type="hidden" name={name} value={currentValue} required={required} />}

      <button
        type="button"
        ref={triggerRef}
        id={selectId}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? listboxId : undefined}
        aria-activedescendant={isOpen && activeIndex >= 0 ? optionId(activeIndex) : undefined}
        aria-invalid={error ? true : undefined}
        aria-required={required || undefined}
        aria-describedby={hasMessage ? messageId : undefined}
        disabled={effectiveDisabled}
        onClick={() => (isOpen ? setIsOpen(false) : open())}
        onKeyDown={handleKeyDown}
        className={clsx(
          'flex w-full items-center justify-between rounded-[10px] border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100',
          error
            ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
            : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700',
          effectiveDisabled && 'cursor-not-allowed bg-gray-50 text-gray-400 dark:bg-gray-900 dark:text-gray-500',
          className,
        )}
      >
        <span
          className={clsx(
            'truncate text-left',
            !selectedOption && 'text-gray-400 dark:text-gray-500',
          )}
        >
          {selectedOption?.label || effectivePlaceholder}
        </span>
        <svg
          className={clsx(
            'h-4 w-4 text-gray-400 transition-transform dark:text-gray-500 flex-shrink-0 ml-2',
            isOpen && 'rotate-180',
          )}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={dropdownRef}
            id={listboxId}
            role="listbox"
            aria-labelledby={label ? labelId : undefined}
            aria-label={label ? undefined : effectivePlaceholder}
            style={dropdownStyle}
            className="max-h-60 overflow-auto rounded-[12px] border border-[var(--co-line,#e5e7eb)] bg-[var(--co-raised,#fff)] p-1 shadow-[var(--co-shadow-pop,0_10px_30px_rgba(0,0,0,.12))]"
          >
            {!hasOptions ? (
              <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                No options available
              </div>
            ) : (
              options.map((option, index) => {
                const isSelected = currentValue === option.value;
                const isActive = index === activeIndex;
                return (
                  <div
                    key={option.value}
                    id={optionId(index)}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={option.disabled || undefined}
                    onClick={() => handleSelect(option)}
                    onMouseEnter={() => !option.disabled && setActiveIndex(index)}
                    className={clsx(
                      'flex items-center justify-between gap-3 rounded-[8px] px-3 py-2 text-[13px] transition-colors',
                      isSelected
                        ? 'bg-[var(--co-brass-wash,#eff6ff)] font-semibold text-[var(--co-brass-ink,#1d4ed8)]'
                        : 'text-[var(--co-ink-2,#374151)]',
                      option.disabled
                        ? 'cursor-not-allowed opacity-60'
                        : 'cursor-pointer',
                      isActive && !isSelected && 'bg-[var(--co-canvas,#f3f4f6)]',
                      isActive && 'outline outline-1 outline-[var(--co-line-strong,#9ca3af)]',
                      !option.disabled && !isSelected && 'hover:bg-[var(--co-canvas,#f3f4f6)]',
                    )}
                  >
                    <span className="truncate">{option.label}</span>
                    {isSelected && (
                      <svg className="h-3.5 w-3.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path
                          fillRule="evenodd"
                          d="M16.704 5.29a1 1 0 010 1.415l-7.5 7.5a1 1 0 01-1.414 0l-3.5-3.5a1 1 0 111.414-1.414L8.5 12.086l6.79-6.796a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                );
              })
            )}
          </div>,
          document.body,
        )}

      {error && <p id={messageId} className="mt-1 text-sm text-red-600">{error}</p>}
      {!error && !hasOptions && (
        <p id={messageId} className="mt-1 text-xs text-gray-500 dark:text-gray-400 italic">
          No options configured for this field.
        </p>
      )}
      {helperText && !error && hasOptions && (
        <p id={messageId} className="mt-1 text-sm text-gray-500 dark:text-gray-400">{helperText}</p>
      )}
    </div>
  );
}
